import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Exercise, Content, Lesson, Unit } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  PenTool,
  AlertCircle,
  Filter,
  Mic,
  Headphones,
  Pencil,
  BookOpen,
  ChevronRight,
  X,
  BarChart3
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExerciseWithRelations extends Exercise {
  content: Content & { lesson: Lesson & { unit: Unit } }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SKILL_ICONS = {
  speaking: Mic,
  listening: Headphones,
  writing: Pencil,
  reading: BookOpen
}

const SKILL_LABELS: Record<string, string> = {
  speaking: 'Habla',
  listening: 'Escucha',
  writing: 'Escritura',
  reading: 'Lectura'
}

const SKILL_COLORS: Record<string, string> = {
  speaking: 'bg-purple-100 text-purple-700',
  listening: 'bg-[#0055A4]/10 text-[#0055A4]',
  writing: 'bg-green-100 text-green-700',
  reading: 'bg-orange-100 text-orange-700'
}

const EXERCISE_TYPE_LABELS: Record<string, string> = {
  multiple_choice: 'Opción Múltiple',
  fill_blank: 'Completar',
  matching: 'Emparejar',
  ordering: 'Ordenar',
  translation: 'Traducción',
  listening: 'Escucha',
  speaking: 'Habla'
}

const EXERCISE_TYPE_COLORS: Record<string, string> = {
  multiple_choice: 'bg-[#0055A4]/5 text-[#0055A4] border-[#0055A4]/20',
  fill_blank: 'bg-amber-50 text-amber-700 border-amber-200',
  matching: 'bg-purple-50 text-purple-700 border-purple-200',
  ordering: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  translation: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  listening: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  speaking: 'bg-pink-50 text-pink-700 border-pink-200'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ExercisesList() {
  // URL search params for cascading filters
  const [searchParams, setSearchParams] = useSearchParams()

  // Local state
  const [exercises, setExercises] = useState<ExerciseWithRelations[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [contents, setContents] = useState<Content[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; exercise: Exercise | null }>({
    open: false,
    exercise: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // ---------------------------------------------------------------------------
  // Derived filter values from URL search params
  // ---------------------------------------------------------------------------

  const filterUnit = searchParams.get('unit') || ''
  const filterLesson = searchParams.get('lesson') || ''
  const filterContent = searchParams.get('content') || ''
  const filterSkill = searchParams.get('skill') || ''

  const setFilterUnit = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('unit', value)
    } else {
      params.delete('unit')
    }
    // Reset downstream filters
    params.delete('lesson')
    params.delete('content')
    setSearchParams(params, { replace: true })
  }

  const setFilterLesson = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('lesson', value)
    } else {
      params.delete('lesson')
    }
    // Reset downstream filter
    params.delete('content')
    setSearchParams(params, { replace: true })
  }

  const setFilterContent = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('content', value)
    } else {
      params.delete('content')
    }
    setSearchParams(params, { replace: true })
  }

  const setFilterSkill = (value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set('skill', value)
    } else {
      params.delete('skill')
    }
    setSearchParams(params, { replace: true })
  }

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true })
    setSearchTerm('')
  }

  // Query string to propagate filters through navigation links
  const filterQuery = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const hasActiveFilters = filterUnit || filterLesson || filterContent || filterSkill

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  useEffect(() => {
    loadData()
  }, [])

  // Load lessons when unit changes
  useEffect(() => {
    if (filterUnit) {
      loadLessons(filterUnit)
    } else {
      setLessons([])
    }
  }, [filterUnit])

  // Load contents when lesson changes
  useEffect(() => {
    if (filterLesson) {
      loadContents(filterLesson)
    } else {
      setContents([])
    }
  }, [filterLesson])

  const loadData = async () => {
    try {
      const [exercisesRes, unitsRes] = await Promise.all([
        supabase
          .from('exercises')
          .select(`
            *,
            content:contents(*, lesson:lessons(*, unit:units(*)))
          `)
          .order('content_id', { ascending: true })
          .order('skill', { ascending: true })
          .order('order_index', { ascending: true }),
        supabase.from('units').select('*').order('unit_number', { ascending: true })
      ])

      if (exercisesRes.error) throw exercisesRes.error
      if (unitsRes.error) throw unitsRes.error

      const mappedExercises = (exercisesRes.data || []).map((e: any) => ({
        ...e,
        question: e.question || e.question_fr || e.question_es || '',
        order_num: e.order_num ?? e.order_index ?? 0,
        content: e.content ? {
          ...e.content,
          title: e.content.title || e.content.title_es || e.content.title_fr || e.content.name_es || e.content.name_fr || '',
          order_num: e.content.order_num ?? e.content.content_number ?? 0,
          lesson: e.content.lesson ? {
            ...e.content.lesson,
            title: e.content.lesson.title || e.content.lesson.title_es || e.content.lesson.title_fr || '',
            order_num: e.content.lesson.order_num ?? e.content.lesson.order_index ?? e.content.lesson.lesson_number ?? 0,
            unit: e.content.lesson.unit ? {
              ...e.content.lesson.unit,
              title: e.content.lesson.unit.title || e.content.lesson.unit.title_es || e.content.lesson.unit.title_fr || e.content.lesson.unit.name_es || e.content.lesson.unit.name_fr || '',
              order_num: e.content.lesson.unit.order_num ?? e.content.lesson.unit.unit_number ?? 0,
            } : null
          } : null
        } : null
      }))

      const mappedUnits = (unitsRes.data || []).map((u: any) => ({
        ...u,
        title: u.title || u.title_es || u.title_fr || u.name_es || u.name_fr || '',
        order_num: u.order_num ?? u.unit_number ?? 0,
      }))

      setExercises(mappedExercises)
      setUnits(mappedUnits)

      // If there is already a unit filter in the URL, load its lessons
      const unitParam = new URLSearchParams(window.location.search).get('unit')
      if (unitParam) {
        await loadLessons(unitParam)
        const lessonParam = new URLSearchParams(window.location.search).get('lesson')
        if (lessonParam) {
          await loadContents(lessonParam)
        }
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadLessons = async (unitId: string) => {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('unit_id', unitId)
        .order('order_index', { ascending: true })

      if (error) throw error

      const mapped = (data || []).map((l: any) => ({
        ...l,
        title: l.title || l.title_es || l.title_fr || l.name_es || l.name_fr || '',
        order_num: l.order_num ?? l.order_index ?? l.lesson_number ?? 0,
      }))
      setLessons(mapped)
    } catch (error) {
      console.error('Error loading lessons:', error)
      setLessons([])
    }
  }

  const loadContents = async (lessonId: string) => {
    try {
      const { data, error } = await supabase
        .from('contents')
        .select('*')
        .eq('lesson_id', lessonId)
        .order('content_number', { ascending: true })

      if (error) throw error

      const mapped = (data || []).map((c: any) => ({
        ...c,
        title: c.title || c.title_es || c.title_fr || c.name_es || c.name_fr || '',
        order_num: c.order_num ?? c.content_number ?? 0,
      }))
      setContents(mapped)
    } catch (error) {
      console.error('Error loading contents:', error)
      setContents([])
    }
  }

  // ---------------------------------------------------------------------------
  // Delete handler
  // ---------------------------------------------------------------------------

  const handleDelete = async () => {
    if (!deleteModal.exercise) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('exercises')
        .delete()
        .eq('id', deleteModal.exercise.id)

      if (error) throw error

      setExercises(exercises.filter(e => e.id !== deleteModal.exercise?.id))
      setDeleteModal({ open: false, exercise: null })
    } catch (error) {
      console.error('Error deleting exercise:', error)
      alert('Error al eliminar el ejercicio')
    } finally {
      setIsDeleting(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------

  const filteredExercises = exercises.filter(exercise => {
    const matchesSearch = (exercise.question || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesUnit = !filterUnit || exercise.content?.lesson?.unit?.id === filterUnit
    const matchesLesson = !filterLesson || exercise.content?.lesson?.id === filterLesson
    const matchesContent = !filterContent || exercise.content_id === filterContent
    const matchesSkill = !filterSkill || exercise.skill === filterSkill
    return matchesSearch && matchesUnit && matchesLesson && matchesContent && matchesSkill
  })

  // ---------------------------------------------------------------------------
  // Breadcrumb helpers
  // ---------------------------------------------------------------------------

  const selectedUnit = units.find(u => u.id === filterUnit)
  const selectedLesson = lessons.find(l => l.id === filterLesson)
  const selectedContent = contents.find(c => c.id === filterContent)

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ejercicios</h1>
          <p className="text-gray-600">Gestiona los ejercicios del curso</p>
        </div>
        <Link
          to={`/admin/exercises/new${filterQuery}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuevo Ejercicio
        </Link>
      </div>

      {/* Breadcrumb - shows current filter location */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0055A4]/5 border border-[#0055A4]/20 rounded-lg text-sm">
          <Filter className="w-4 h-4 text-[#0055A4] shrink-0" />
          <div className="flex items-center gap-1.5 flex-wrap text-[#003d7a]">
            {selectedUnit && (
              <>
                <span className="font-medium">
                  Unidad {selectedUnit.order_num}{selectedUnit.title ? `: ${selectedUnit.title}` : ''}
                </span>
                {(selectedLesson || selectedContent || filterSkill) && (
                  <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
                )}
              </>
            )}
            {selectedLesson && (
              <>
                <span className="font-medium">
                  Lección {selectedLesson.order_num}{selectedLesson.title ? `: ${selectedLesson.title}` : ''}
                </span>
                {(selectedContent || filterSkill) && (
                  <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
                )}
              </>
            )}
            {selectedContent && (
              <>
                <span className="font-medium">Contenido {selectedContent.order_num}</span>
                {filterSkill && (
                  <ChevronRight className="w-3.5 h-3.5 text-blue-400" />
                )}
              </>
            )}
            {filterSkill && (
              <span className="font-medium">{SKILL_LABELS[filterSkill] || filterSkill}</span>
            )}
          </div>
          <button
            onClick={clearAllFilters}
            className="ml-auto p-1 hover:bg-[#0055A4]/10 rounded transition-colors text-[#0055A4]"
            title="Limpiar filtros"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Text search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por pregunta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          />
        </div>

        {/* Unit filter */}
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterUnit}
            onChange={(e) => setFilterUnit(e.target.value)}
            className="pl-9 pr-8 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] appearance-none bg-white text-sm"
          >
            <option value="">Todas las unidades</option>
            {units.map(unit => (
              <option key={unit.id} value={unit.id}>
                Unidad {unit.order_num}{unit.title ? `: ${unit.title}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Lesson filter - visible when unit is selected */}
        {filterUnit && (
          <select
            value={filterLesson}
            onChange={(e) => setFilterLesson(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] bg-white text-sm"
          >
            <option value="">Todas las lecciones</option>
            {lessons.map(lesson => (
              <option key={lesson.id} value={lesson.id}>
                Lección {lesson.order_num}: {lesson.title}
              </option>
            ))}
          </select>
        )}

        {/* Content filter - visible when lesson is selected */}
        {filterLesson && (
          <select
            value={filterContent}
            onChange={(e) => setFilterContent(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] bg-white text-sm"
          >
            <option value="">Todos los contenidos</option>
            {contents.map(content => (
              <option key={content.id} value={content.id}>
                Contenido {content.order_num}: {content.title}
              </option>
            ))}
          </select>
        )}

        {/* Skill filter */}
        <select
          value={filterSkill}
          onChange={(e) => setFilterSkill(e.target.value)}
          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] bg-white text-sm"
        >
          <option value="">Todas las habilidades</option>
          <option value="speaking">Habla</option>
          <option value="listening">Escucha</option>
          <option value="writing">Escritura</option>
          <option value="reading">Lectura</option>
        </select>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-600">
        <BarChart3 className="w-4 h-4 text-gray-400" />
        <span>
          <span className="font-semibold text-gray-900">{filteredExercises.length}</span>
          {' '}ejercicio{filteredExercises.length !== 1 ? 's' : ''}
          {hasActiveFilters || searchTerm ? ' encontrado' + (filteredExercises.length !== 1 ? 's' : '') : ' en total'}
        </span>
        {(hasActiveFilters || searchTerm) && (
          <span className="text-gray-400">
            (de {exercises.length} totales)
          </span>
        )}
      </div>

      {/* Exercises Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ejercicio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Habilidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ubicación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Puntos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredExercises.length > 0 ? (
                filteredExercises.map((exercise, index) => {
                  const SkillIcon = SKILL_ICONS[exercise.skill as keyof typeof SKILL_ICONS] || PenTool
                  const unitNum = exercise.content?.lesson?.unit?.order_num ?? '?'
                  const lessonNum = exercise.content?.lesson?.order_num ?? '?'
                  const contentNum = exercise.content?.order_num ?? '?'
                  const exerciseType = exercise.exercise_type || exercise.type || ''

                  return (
                    <tr key={exercise.id} className="hover:bg-gray-50 transition-colors">
                      {/* Index number */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                          {index + 1}
                        </span>
                      </td>

                      {/* Question */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3 max-w-sm">
                          <div className="p-2 rounded-lg bg-pink-100 shrink-0">
                            <PenTool className="w-4 h-4 text-pink-600" />
                          </div>
                          <p className="font-medium text-gray-900 line-clamp-2 text-sm leading-snug">
                            {exercise.question || '(Sin pregunta)'}
                          </p>
                        </div>
                      </td>

                      {/* Exercise type badge */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${
                          EXERCISE_TYPE_COLORS[exerciseType] || 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {EXERCISE_TYPE_LABELS[exerciseType] || exerciseType.replaceAll('_', ' ')}
                        </span>
                      </td>

                      {/* Skill badge */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
                          SKILL_COLORS[exercise.skill] || 'bg-gray-100 text-gray-700'
                        }`}>
                          <SkillIcon className="w-3.5 h-3.5" />
                          {SKILL_LABELS[exercise.skill] || exercise.skill}
                        </span>
                      </td>

                      {/* Location */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded font-mono">
                          U{unitNum} L{lessonNum} C{contentNum}
                        </span>
                      </td>

                      {/* Points */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                          {exercise.points} pts
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          exercise.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {exercise.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/admin/exercises/${exercise.id}${filterQuery}`}
                            className="p-2 text-gray-500 hover:text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                            title="Visualizar"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/admin/exercises/${exercise.id}/edit${filterQuery}`}
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => setDeleteModal({ open: true, exercise })}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <PenTool className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-1">No se encontraron ejercicios</p>
                    {(hasActiveFilters || searchTerm) ? (
                      <button
                        onClick={clearAllFilters}
                        className="text-[#0055A4] hover:underline text-sm"
                      >
                        Limpiar filtros
                      </button>
                    ) : (
                      <Link
                        to={`/admin/exercises/new${filterQuery}`}
                        className="text-[#0055A4] hover:underline text-sm inline-block"
                      >
                        Crear primer ejercicio
                      </Link>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Ejercicio</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar este ejercicio?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, exercise: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
